// src/pages/WorkDetail.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../api/supabaseClient';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  useMediaQuery,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  MenuBook as StoryIcon,
} from '@mui/icons-material';

const WorkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [work, setWork] = useState(null);
  const [images, setImages] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab indices: 0 = Stories, 1 = Images
  const [tabIndex, setTabIndex] = useState(0);
  const isSmallUp = useMediaQuery('(min-width:600px)');

  // Image lightbox state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogIndex, setImageDialogIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1) Fetch work
        const { data: wdata, error: workErr } = await supabase
            .from('works')
            .select('*')
            .eq('id', id)
            .single();
        if (workErr || !wdata) {
          navigate('/works');
          return;
        }
        setWork(wdata);

        // 2) Fetch images
        const { data: imgs } = await supabase
            .from('images')
            .select('*')
            .eq('work_id', id)
            .order('inserted_at', { ascending: true });
        setImages(imgs || []);

        // 3) Fetch stories
        const { data: strs } = await supabase
            .from('stories')
            .select('*')
            .eq('work_id', id)
            .order('created_at', { ascending: true });
        setStories(strs || []);
      } catch (err) {
        setError(err.message || t('unexpectedError'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, t]);

  // Lightbox kontrolleri
  const openImageDialog = (index) => {
    setImageDialogIndex(index);
    setImageDialogOpen(true);
  };
  const closeImageDialog = () => setImageDialogOpen(false);

  const showPrevImage = () =>
      setImageDialogIndex((prev) => (prev - 1 + images.length) % images.length);
  const showNextImage = () =>
      setImageDialogIndex((prev) => (prev + 1) % images.length);

  // Klavye desteği (ok ve ESC)
  const handleKeyDown = useCallback(
      (e) => {
        if (!imageDialogOpen) return;
        if (e.key === 'ArrowLeft') showPrevImage();
        if (e.key === 'ArrowRight') showNextImage();
        if (e.key === 'Escape') closeImageDialog();
      },
      [imageDialogOpen, images.length, imageDialogIndex]
  );

  useEffect(() => {
    if (imageDialogOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [imageDialogOpen, handleKeyDown]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tabIndex]);

  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  if (error) {
    return (
        <Container sx={{ mt: 4 }}>
          <Typography color="error">{error}</Typography>
        </Container>
    );
  }

  return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* — Hero Bölümü — */}
        <Paper elevation={3} sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          {images[0] ? (
              <Box
                  sx={{
                    position: 'relative',
                    height: isSmallUp ? 400 : 250,
                    backgroundImage: `url(${images[0].url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
              >
                <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                    }}
                />
                <Typography
                    variant={isSmallUp ? 'h2' : 'h4'}
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 24,
                      color: '#fff',
                      fontWeight: 700,
                    }}
                >
                  {i18n.language === 'tr' ? work.title_tr : work.title_en}
                </Typography>
              </Box>
          ) : (
              <Box sx={{ p: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {i18n.language === 'tr' ? work.title_tr : work.title_en}
                </Typography>
              </Box>
          )}
        </Paper>

        {/* — Back Button — */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
          >
            {t('back')}
          </Button>
        </Box>

        {/* — Tabs — */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
              value={tabIndex}
              onChange={(e, val) => setTabIndex(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
          >
            <Tab label={t('stories')} />
            <Tab label={t('images')} />
          </Tabs>
        </Box>

        {/* — Stories Tab — */}
        {tabIndex === 0 && (
            <Box>
              {stories.length > 0 ? (
                  stories.map((st) => (
                      <Card
                          key={st.id}
                          elevation={0}
                          sx={{
                            mb: 2,
                            borderRadius: 1,
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            maxHeight: 300,
                            overflowY: 'auto',
                          }}
                      >
                        <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <StoryIcon
                              sx={{
                                fontSize: isSmallUp ? 32 : 24,
                                color: 'primary.main',
                              }}
                          />
                          <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "'Roboto', sans-serif",
                                fontSize: isSmallUp ? '1rem' : '0.875rem',
                                lineHeight: 1.5,
                                color: '#333',
                                whiteSpace: 'pre-wrap',
                              }}
                          >
                            {i18n.language === 'tr' ? st.story_tr : st.story_en}
                          </Typography>
                        </CardContent>
                      </Card>
                  ))
              ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('noStories')}
                  </Typography>
              )}
            </Box>
        )}

        {/* — Images Tab — */}
        {tabIndex === 1 && (
            <Box>
              {images.length > 0 ? (
                  <ImageList
                      cols={isSmallUp ? 3 : 1}
                      gap={16}
                      sx={{
                        mb: 4,
                        '& .MuiImageListItem-root': {
                          cursor: 'pointer',
                          position: 'relative',
                          '&:hover .story-indicator': { opacity: 1 },
                        },
                      }}
                  >
                    {images.map((img, idx) => (
                        <ImageListItem key={img.id} onClick={() => openImageDialog(idx)}>
                          <img
                              src={`${img.url}?w=600&fit=crop&auto=format`}
                              srcSet={`${img.url}?w=600&fit=crop&auto=format&dpr=2 2x`}
                              alt={t('workImage')}
                              loading="lazy"
                              style={{ borderRadius: 8 }}
                          />
                        </ImageListItem>
                    ))}
                  </ImageList>
              ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('noImages')}
                  </Typography>
              )}

              {/* — Full Screen Image Lightbox Dialog — */}
              <Dialog
                  open={imageDialogOpen}
                  onClose={closeImageDialog}
                  fullScreen
                  PaperProps={{
                    sx: { bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center' },
                  }}
              >
                <DialogContent
                    sx={{
                      p: 0,
                      position: 'relative',
                      width: '100vw',
                      height: '100vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'black',
                    }}
                >
                  {/* Close Button */}
                  <IconButton
                      sx={{
                        position: 'absolute',
                        top: 24,
                        right: 32,
                        color: 'white',
                        zIndex: 10,
                        backgroundColor: 'rgba(0,0,0,0.5)'
                      }}
                      onClick={closeImageDialog}
                      aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                  {/* Previous Button */}
                  <IconButton
                      sx={{
                        position: 'absolute',
                        left: 32,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'white',
                        zIndex: 10,
                        backgroundColor: 'rgba(0,0,0,0.3)'
                      }}
                      onClick={showPrevImage}
                      aria-label="previous"
                  >
                    <ChevronLeftIcon fontSize="large" />
                  </IconButton>
                  {/* The Image */}
                  <Box
                      component="img"
                      src={images[imageDialogIndex]?.url}
                      alt={t('enlargedImage')}
                      sx={{
                        maxWidth: '96vw',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        mx: 'auto',
                        my: 'auto',
                        borderRadius: 2,
                        boxShadow: 4,
                      }}
                  />
                  {/* Next Button */}
                  <IconButton
                      sx={{
                        position: 'absolute',
                        right: 32,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'white',
                        zIndex: 10,
                        backgroundColor: 'rgba(0,0,0,0.3)'
                      }}
                      onClick={showNextImage}
                      aria-label="next"
                  >
                    <ChevronRightIcon fontSize="large" />
                  </IconButton>
                </DialogContent>
              </Dialog>
            </Box>
        )}
      </Container>
  );
};

export default WorkDetail;
